/**
 * Which words a Draft email starts on, and where it is allowed to get them.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A TASK NAMES ITS CONTENT IN PROSE. THE LINK IS `enrollment_step_id` (crm#16)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `advance_enrollments()` writes a task's text as
 * `format('Step %s: %s', d.step_no, coalesce(d.content_name, ...))`, so the
 * only thing the row says about its content is a rendered sentence -- there is
 * no `content_id` on `tasks` and there is not going to be one. The real link is
 * `tasks.enrollment_step_id -> enrollment_steps.content_id`, and it is
 * one-to-one: `tasks_enrollment_step_id_key` is a UNIQUE partial index. Proven
 * against production, 2026-09-06:
 *
 *     task 5987 "Step 1: Trade show follow-up"
 *       -> enrollment_step 2000 -> content_id 1 -> "Trade show follow-up"
 *
 * NEVER BY MATCHING THE NAME IN THE TEXT. It looks like it would work on that
 * row, and it is wrong in both directions: two assets may carry the same name
 * (nothing in the schema forbids it), the text is truncated prose a person may
 * have edited, and a step whose content was RE-POINTED afterwards still shows
 * the old name in the task it already generated. Resolution here is by id or
 * it does not happen -- see preselectedTemplateId, which is handed the step and
 * never the task's text.
 *
 * WHAT THE OLD DEFAULT DID. `TouchButtons.tsx` preselected `templates[0]` --
 * the alphabetically first active email asset -- and `draftEmailAndStamp` then
 * wrote that id onto the engagement as `content_id`. So someone working
 * "Step 4: The TTB angle" was handed a different template, and if they did not
 * notice, the record answered "which version did we send them" with the wrong
 * one. A missing link is visibly missing; a wrong default is invisibly wrong,
 * which is why this is the half of crm#16 that mattered.
 */

import type { ContentAsset } from "./ContentLibrary";
import { effectiveContent, type StepContent } from "./stepContent";
import type { EnrollmentStep } from "./types";

/** The step columns a draft reads. Its id is never used to pick content. */
export type DraftStep = Pick<
  EnrollmentStep,
  "id" | "content_id" | "subject" | "body"
>;

/** The asset columns the picker and the preview read. */
export type DraftTemplate = Pick<
  ContentAsset,
  "id" | "name" | "subject" | "body"
>;

/**
 * The options the picker offers, with the step's OWN asset guaranteed present.
 *
 * The list query filters `is_active: true`, and an asset is retired by
 * clearing that flag rather than deleted (`content_assets` says so on its own
 * screen, because `enrollment_steps.content_id` references it). So a step
 * seeded months ago can name an asset the picker would not otherwise offer --
 * and a preselected id with no matching `<SelectItem>` renders as an EMPTY
 * trigger, which reads exactly like "nothing was chosen" while the step had a
 * perfectly good opinion.
 *
 * Inserted in name order rather than appended, because the query is sorted by
 * name and one entry out of order at the bottom of the list reads as a bug.
 * Everything else keeps the order the server sent.
 */
export function templateOptions<T extends DraftTemplate>(
  templates: readonly T[] | undefined,
  stepAsset: T | null | undefined,
): T[] {
  const list = [...(templates ?? [])];
  if (!stepAsset) return list;
  if (list.some((t) => String(t.id) === String(stepAsset.id))) return list;
  const at = list.findIndex((t) => t.name.localeCompare(stepAsset.name) > 0);
  if (at === -1) list.push(stepAsset);
  else list.splice(at, 0, stepAsset);
  return list;
}

/**
 * The template a draft must OPEN on, or "" for "nobody has chosen one".
 *
 * "" IS THE ANSWER WITH NO STEP, AND IT IS THE POINT OF THIS FUNCTION. From
 * the contact page there is no step, so there is nothing to preselect and the
 * dialog must require an explicit choice -- the app never records an asset the
 * user did not pick. Falling back to `templates[0]` here would put crm#16
 * straight back.
 *
 * "" ALSO WHILE THE ASSET IS STILL LOADING. The step resolves before the asset
 * it names, so for a render or two `options` genuinely does not contain it;
 * naming an id the picker cannot show would blank the trigger. Callers derive
 * this on every render rather than latching it in an effect, so the preselect
 * appears the moment the asset does.
 */
export function preselectedTemplateId(
  step: DraftStep | null | undefined,
  options: readonly DraftTemplate[] | undefined,
): string {
  if (!step || step.content_id == null) return "";
  const id = String(step.content_id);
  return (options ?? []).some((t) => String(t.id) === id) ? id : "";
}

/**
 * The subject and body to render, honoring THIS contact's own override.
 *
 * `enrollment_steps.subject`/`body` are the per-contact override migration 081
 * added; null means "use the asset", and the two fall back independently. The
 * whole point of that table is that the words one person gets can be edited
 * without touching the library row, so a draft that ignored the override would
 * send the generic version of a step somebody had deliberately rewritten.
 *
 * THE OVERRIDE APPLIES ONLY TO THE STEP'S OWN ASSET. Someone who changes the
 * picker to a different template has chosen different words on purpose;
 * pouring step 4's override into template 9 would produce a message that
 * belongs to neither. So the identity check is on `content_id`, and a mismatch
 * falls back to the asset as written.
 */
export function draftContent(
  step: DraftStep | null | undefined,
  selected: DraftTemplate | null | undefined,
): StepContent | null {
  if (!selected) return null;
  const ownsIt =
    step != null &&
    step.content_id != null &&
    String(step.content_id) === String(selected.id);
  if (ownsIt) return effectiveContent(step, selected);
  return { subject: selected.subject, body: selected.body, source: "asset" };
}
